import BigNumber from 'bignumber.js'
import * as R from 'radash'

import { type Equivalence } from '@blade/std-lib'

import type { Column } from '../ui/data-table-types'

export type NumericFilter<Column extends string> = {
  type: 'numeric'
  column: Column
  operator: 'eq' | 'gt' | 'gte' | 'lt' | 'lte'
  value: number
}

export type StringFilter<Column extends string> = {
  type: 'string'
  column: Column
  operator: 'eq' | 'like'
  value: string
}

export type CustomFilter<Column extends string, Value = unknown> = {
  type: 'custom'
  column: Column
  value: Value
  predicate: (value: Value) => boolean
}

export type CompositeFilter<Column extends string> = {
  type: 'composite'
  operator: 'and' | 'or'
  filters: Array<TableFilter<Column>>
}

export type TableFilter<Column extends string, Value = unknown> =
  | NumericFilter<Column>
  | CustomFilter<Column, Value>
  | StringFilter<Column>
  | CompositeFilter<Column>

export type TableFilters<Column extends string = string> = {
  [key in Column]?: TableFilter<Column> | undefined
}

export const tryExtractStringFilterValue = <Column extends string>(
  filter: TableFilter<Column>,
): string | undefined => {
  if (filter.type === 'string') {
    return filter.value
  }
}

export const eq: Equivalence.Equivalence<TableFilter<string> | undefined> = (
  left,
  right,
) => {
  return R.isEqual(left, right)
}

export const hasAnyFilter = (filters: TableFilters<string>): boolean => {
  return Object.values(filters).some((filter) => filter !== undefined)
}

export const test =
  <ColId extends string, D extends object>(
    filter: TableFilter<ColId>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    columns: Array<Column<D, ColId, any>>,
  ) =>
  (item: D): boolean => {
    if (filter.type === 'composite') {
      if (filter.operator === 'and') {
        return filter.filters.every((f) => test(f, columns)(item))
      }
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (filter.operator === 'or') {
        return filter.filters.some((f) => test(f, columns)(item))
      }
      return false
    }

    const column = columns.find((c) => c.id === filter.column)
    const itemValue: unknown = column?.getValue
      ? column.getValue(item)
      : // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (item as any)[filter.column]

    if (filter.type === 'string') {
      if (filter.operator === 'eq') {
        return itemValue === filter.value
      }
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (filter.operator === 'like' && typeof itemValue === 'string') {
        return itemValue.includes(filter.value)
      }

      return false
    }

    if (filter.type === 'numeric') {
      if (typeof itemValue === 'number' || BigNumber.isBigNumber(itemValue)) {
        const bn = BigNumber(itemValue)
        if (filter.operator === 'eq') {
          return bn.eq(filter.value)
        }
        if (filter.operator === 'gt') {
          return bn.gt(filter.value)
        }
        if (filter.operator === 'gte') {
          return bn.gte(filter.value)
        }
        if (filter.operator === 'lt') {
          return bn.lt(filter.value)
        }
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (filter.operator === 'lte') {
          return bn.lte(filter.value)
        }
      }

      return false
    }

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (filter.type === 'custom') {
      return filter.predicate(itemValue)
    }
    const _checkExhaustive: never = filter

    return false
  }

export const composeFilters = <Column extends string>(
  filters: TableFilters<Column>,
): TableFilter<Column> | undefined => {
  const existingFilters = (
    Object.values(filters) as Array<TableFilter<Column> | undefined>
  ).filter((it) => it !== undefined)

  if (existingFilters.length === 0) {
    return undefined
  }

  if (existingFilters.length === 1) {
    return existingFilters[0]
  }

  return {
    type: 'composite',
    operator: 'and',
    filters: existingFilters,
  }
}
